import { VPNService } from '../luigiNodes/vpn.service';
import { ExtensionTrustHandlingService } from './extension-trust-handling.service';
import { PluginHandlingService } from './plugin-handling.service';
import { Injectable } from '@angular/core';
import { Context } from '@luigi-project/client';
import {
  ClientEnvironment,
  ConfigService,
  CustomNodeProcessingService,
  EnvConfigService,
  LuigiNode,
  NetworkVisibility,
  PortalConfig,
  matchesJMESPath,
} from '@openmfp/portal-ui-lib';
import { JukeboxLuigiNode } from 'src/app/models/luigi';

const CONFIGURATION_MISSING_HANDLING_PATH_PATH =
  '/configuration-missing-handling';

@Injectable({
  providedIn: 'root',
})
export class DxpCustomNodeProcessingService
  implements CustomNodeProcessingService
{
  constructor(
    private vpnService: VPNService,
    private pluginHandlingService: PluginHandlingService,
    private configService: ConfigService,
    private envConfigService: EnvConfigService,
    private trustExtensionHandlingService: ExtensionTrustHandlingService,
  ) {}

  public async processNode(
    ctx: Context,
    node: JukeboxLuigiNode,
  ): Promise<JukeboxLuigiNode> {
    const portalConfig: PortalConfig =
      await this.configService.getPortalConfig();
    const clientEnvironment: ClientEnvironment =
      await this.envConfigService.getEnvConfig();

    let newNode = node;
    /* The name for this condition is misleading as it does not guard node access.
     * We need the breadcrumb bar for project level extensions that must be trusted.
     * As a workaround we intercept the showBreadcrumb functionality and hide only the
     * path of the breadcrumb bar in such cases.
     */
    if (
      !(node.showBreadcrumbs == undefined) &&
      !node.showBreadcrumbs &&
      node.isProjectLevelExtension
    ) {
      node.hideBreadcrumbPath = true;
      node.showBreadcrumbs = true;
    }
    if (newNode.isMissingMandatoryData) {
      newNode = this.missingMandatoryDataHandling(newNode, portalConfig);
    }
    if (newNode.configurationMissing) {
      newNode = this.requiresConfigurationHandling(ctx, newNode);
    }
    if (newNode.networkVisibility === NetworkVisibility.INTERNAL) {
      newNode = this.vpnService.vpnHandling(newNode);
    }
    if (newNode.visibleForPlugin) {
      newNode = this.pluginHandlingService.pluginHandling(
        newNode,
        clientEnvironment,
      );
    }

    if (
      newNode?.isProjectLevelExtension &&
      !this.trustExtensionHandlingService.isTrustedProject(
        ctx['entityContext'].project.id,
      )
    ) {
      newNode =
        this.trustExtensionHandlingService.convertToNoTrustHandlingNode(
          newNode,
        );
    }
    return newNode;
  }

  private missingMandatoryDataHandling(
    node: LuigiNode,
    portalConfig: PortalConfig,
  ): LuigiNode {
    // Extension Manager CDM has the :extClassName as part of it, but we have to
    // replace it with an actual value so we cut it off here
    const extensionManagerMissingMandatoryDataUrl =
      portalConfig.portalContext.extensionManagerMissingMandatoryDataUrl?.replace(
        ':extClassName',
        node.extensionClassName,
      );

    return Object.assign({}, node, {
      viewUrl: extensionManagerMissingMandatoryDataUrl,
      visibleForPlugin: undefined,
      configurationMissing: undefined,
      children: [],
      networkVisibility: undefined,
      isolateView: true,
      loadingIndicator: { enabled: false },
    });
  }

  private requiresConfigurationHandling(
    ctx: Context,
    node: LuigiNode,
  ): LuigiNode {
    if (!matchesJMESPath(ctx, node.configurationMissing ?? '')) {
      return node;
    }
    return Object.assign({}, node, {
      viewUrl: CONFIGURATION_MISSING_HANDLING_PATH_PATH,
      context: {
        configurationHint: node.configurationHint,
        configurationLink: node.configurationLink,
      },
      visibleForPlugin: undefined,
      isMissingMandatoryData: undefined,
      children: [],
      networkVisibility: undefined,
      isolateView: true,
      loadingIndicator: { enabled: false },
    });
  }
}
